import { auth } from '../lib/firebase';
import { AI_CATALOG } from '../data/ai-catalog';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AIQuestion {
  text: string;
  type: 'choix' | 'numerique' | 'oui_non';
  why: string;
  options?: string[];
}

export interface AISelectedItem {
  key: string;
  reason: string;
  strength: 'recommended' | 'optional';
}

// ── Internal helpers ──────────────────────────────────────────────────────────

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Non authentifié');
  return user.getIdToken();
}

async function callAIFunction(body: Record<string, unknown>): Promise<unknown> {
  const token = await getAuthToken();

  const response = await fetch('/.netlify/functions/ai-project-analysis', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await response.json();

  if (!response.ok) {
    if (json?.error === 'service_unavailable') {
      const err = new Error(json?.message || 'Le service IA est temporairement surchargé. Veuillez réessayer dans quelques instants.');
      (err as any).code = 'service_unavailable';
      throw err;
    }
    throw new Error(json?.error || json?.message || `Erreur serveur (${response.status})`);
  }

  return json.result;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function getProjectQuestions(projectDescription: string): Promise<AIQuestion[]> {
  const result = await callAIFunction({
    mode: 'questions',
    projectDescription,
  });

  const parsed = result as { questions?: AIQuestion[] };
  if (!parsed?.questions || !Array.isArray(parsed.questions)) {
    throw new Error('Réponse inattendue du serveur (champ "questions" manquant)');
  }

  return parsed.questions;
}

export async function getProjectMapping(
  projectDescription: string,
  answers: Record<string, string>
): Promise<AISelectedItem[]> {
  const result = await callAIFunction({
    mode: 'mapping',
    projectDescription,
    answers,
    catalog: AI_CATALOG,
  });

  const parsed = result as { items?: AISelectedItem[] };
  if (!parsed?.items || !Array.isArray(parsed.items)) {
    throw new Error('Réponse inattendue du serveur (champ "items" manquant)');
  }

  // Filter out any keys that don't exist in the catalog (LLM hallucination guard)
  const validKeys = new Set(AI_CATALOG.map((c) => c.key));
  return parsed.items.filter((item) => validKeys.has(item.key));
}
